import jsPDF from 'jspdf';
import type { SavedQuery } from '../types/query';
import { AUTOMATION_INTERVALS } from '../types/query';
import { queryService } from './queryService';

export const documentationService = {
  generatePDF(query: SavedQuery) {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = margin;

    const endpointUrl = queryService.generateEndpointUrl(query.endpoint_slug);
    const intervalLabel = AUTOMATION_INTERVALS.find(i => i.value === query.automation_interval)?.label;

    // Header
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(query.name, margin, 12);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR')} as ${new Date().toLocaleTimeString('pt-BR')}`, margin, 18);

    yPos = 30;
    doc.setTextColor(0, 0, 0);

    // Status badges
    doc.setFontSize(7);
    let badgeX = margin;

    if (query.is_active) {
      doc.setFillColor(209, 250, 229);
      doc.setDrawColor(209, 250, 229);
      doc.roundedRect(badgeX, yPos, 15, 5, 1, 1, 'FD');
      doc.setTextColor(6, 95, 70);
      doc.text('Ativo', badgeX + 2, yPos + 3.5);
      badgeX += 17;
    }

    if (query.automation_enabled) {
      doc.setFillColor(204, 251, 241);
      doc.setDrawColor(204, 251, 241);
      doc.roundedRect(badgeX, yPos, 25, 5, 1, 1, 'FD');
      doc.setTextColor(17, 94, 89);
      doc.text('Automacao', badgeX + 2, yPos + 3.5);
      badgeX += 27;
    }

    if (query.availability_type === 'api' || query.availability_type === 'both' || !query.availability_type) {
      doc.setFillColor(219, 234, 254);
      doc.setDrawColor(219, 234, 254);
      doc.roundedRect(badgeX, yPos, 15, 5, 1, 1, 'FD');
      doc.setTextColor(30, 64, 175);
      doc.text('API', badgeX + 2, yPos + 3.5);
    }

    yPos += 8;
    doc.setTextColor(0, 0, 0);

    // Description
    if (query.description) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const descLines = doc.splitTextToSize(query.description, contentWidth);
      doc.text(descLines.slice(0, 2), margin, yPos);
      yPos += descLines.slice(0, 2).length * 3.5 + 2;
    }

    // Endpoint
    if (query.availability_type === 'api' || query.availability_type === 'both' || !query.availability_type) {
      doc.setFillColor(239, 246, 255);
      doc.setDrawColor(191, 219, 254);
      doc.roundedRect(margin, yPos, contentWidth, 10, 1, 1, 'FD');

      doc.setFillColor(16, 185, 129);
      doc.roundedRect(margin + 2, yPos + 2, 10, 4, 0.5, 0.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('GET', margin + 3.5, yPos + 4.8);

      doc.setTextColor(30, 64, 175);
      doc.setFontSize(7);
      doc.setFont('courier', 'normal');
      const endpointLines = doc.splitTextToSize(endpointUrl, contentWidth - 15);
      doc.text(endpointLines[0], margin + 14, yPos + 5);

      yPos += 12;
    }

    // SQL Query
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Query SQL:', margin, yPos);
    yPos += 4;

    doc.setFillColor(31, 41, 55);
    const sqlHeight = Math.min(25, 4 + (query.sql_query.split('\n').length * 2.5));
    doc.roundedRect(margin, yPos, contentWidth, sqlHeight, 1, 1, 'F');

    doc.setTextColor(249, 250, 251);
    doc.setFontSize(6.5);
    doc.setFont('courier', 'normal');
    const sqlLines = doc.splitTextToSize(query.sql_query, contentWidth - 4);
    const maxSqlLines = Math.floor((sqlHeight - 2) / 2.5);
    doc.text(sqlLines.slice(0, maxSqlLines), margin + 2, yPos + 3);
    yPos += sqlHeight + 3;

    // Two columns for remaining content
    const colWidth = (contentWidth - 3) / 2;
    let leftY = yPos;
    let rightY = yPos;

    // Left column - Campos Retornados
    if (query.selected_columns.length > 0) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Campos Retornados:', margin, leftY);
      leftY += 4;

      doc.setFontSize(6.5);
      doc.setFont('courier', 'normal');
      const cols = query.selected_columns.slice(0, 12);
      cols.forEach((col) => {
        if (leftY < pageHeight - 40) {
          doc.setFillColor(243, 244, 246);
          doc.roundedRect(margin, leftY - 2, colWidth - 2, 3.5, 0.5, 0.5, 'F');
          doc.setTextColor(31, 41, 55);
          const colText = col.length > 25 ? col.substring(0, 25) + '...' : col;
          doc.text(colText, margin + 1, leftY);
          leftY += 4;
        }
      });
      leftY += 2;
    }

    // Right column - Filtros
    if (query.filter_columns && query.filter_columns.length > 0) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Campos de Filtro:', pageWidth / 2 + 2, rightY);
      rightY += 4;

      doc.setFontSize(6.5);
      doc.setFont('courier', 'normal');
      const filters = query.filter_columns.slice(0, 12);
      filters.forEach((col) => {
        if (rightY < pageHeight - 40) {
          doc.setFillColor(220, 252, 231);
          doc.roundedRect(pageWidth / 2 + 2, rightY - 2, colWidth - 2, 3.5, 0.5, 0.5, 'F');
          doc.setTextColor(22, 101, 52);
          const colText = col.length > 25 ? col.substring(0, 25) + '...' : col;
          doc.text(colText, pageWidth / 2 + 3, rightY);
          rightY += 4;
        }
      });
      rightY += 2;
    }

    yPos = Math.max(leftY, rightY);

    // Automation info
    if (query.automation_enabled && yPos < pageHeight - 30) {
      doc.setFillColor(240, 253, 250);
      doc.setDrawColor(153, 246, 228);
      const autoHeight = 12;
      doc.roundedRect(margin, yPos, contentWidth, autoHeight, 1, 1, 'FD');

      doc.setTextColor(17, 94, 89);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('Automacao:', margin + 2, yPos + 4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.text(`Frequencia: ${intervalLabel || query.automation_interval}`, margin + 2, yPos + 8);

      yPos += autoHeight + 3;
    }

    // Integrations
    if ((query.integration_solace_enabled || query.integration_sap_btp_enabled) && yPos < pageHeight - 25) {
      doc.setFillColor(255, 247, 237);
      doc.setDrawColor(254, 215, 170);
      const intHeight = 10;
      doc.roundedRect(margin, yPos, contentWidth, intHeight, 1, 1, 'FD');

      doc.setTextColor(146, 64, 14);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      const integrations = [];
      if (query.integration_solace_enabled) integrations.push('Solace Event Broker');
      if (query.integration_sap_btp_enabled) integrations.push('SAP BTP');
      doc.text(`Integracoes: ${integrations.join(', ')}`, margin + 2, yPos + 4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      if (query.integration_solace_enabled && query.integration_solace_queue_name) {
        doc.text(`Queue: ${query.integration_solace_queue_name}`, margin + 2, yPos + 7.5);
      }

      yPos += intHeight + 2;
    }

    // Footer
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc.setTextColor(107, 114, 128);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Gerenciador de Serviços - Documentacao Automatica', margin, pageHeight - 10);
    doc.text(`Atualizado em ${new Date(query.updated_at).toLocaleDateString('pt-BR')}`, pageWidth - margin - 30, pageHeight - 10);

    // Save PDF
    const filename = `${query.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_doc.pdf`;
    doc.save(filename);
  }
};
